import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import TrustBadges from "@/components/TrustBadges";

import CookieBanner from "@/components/CookieBanner";
import SEOHead from "@/components/SEOHead";
import SeasonalBanner from "@/components/SeasonalBanner";
import LoyaltySignupForm from "@/components/LoyaltySignupForm";
import { LocalBusinessSchema, BreadcrumbSchema, FAQSchema, OrganizationSchema, AggregateReviewsSchema, ServiceAreaSchema } from "@/components/SchemaMarkup";
import { Scissors, Heart, Sparkles, Cat, Award, Shield, Clock, Star, ArrowRight, CheckCircle, MapPin, Phone } from "lucide-react";
import { trackCTAClick, trackPhoneClick, trackFAQInteraction, trackNavigation, trackGalleryInteraction } from "@/lib/analytics";
import TransformationShowcase from "@/components/TransformationShowcase";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import HappyPetsGallery from "@/components/HappyPetsGallery";
import groomingVan from "@/assets/mobile-dog-grooming-van-denver.jpg";
import happyDog from "@/assets/happy-dog-after-mobile-grooming-denver.jpg";
import beforeGolden from "@/assets/golden-retriever-before-grooming-denver.jpg";
import afterGolden from "@/assets/golden-retriever-after-grooming-denver.jpg";
import beforePoodle from "@/assets/poodle-before-grooming-transformation-denver.jpg";
import afterPoodle from "@/assets/poodle-after-grooming-transformation-denver.jpg";
import beforeHavanese from "@/assets/havanese-before-grooming-denver.jpg";
import afterHavanese from "@/assets/havanese-after-grooming-denver.jpg";
import beforeCat from "@/assets/cat-grooming-before-transformation-denver.jpg";
import afterCat from "@/assets/cat-grooming-after-transformation-denver.jpg";
import beforeLabradoodleV2 from "@/assets/labradoodle-before-grooming-denver-v2.jpg";
import afterLabradoodleV2 from "@/assets/labradoodle-after-grooming-denver-v2.jpg";
import beforeLabradoodle from "@/assets/labradoodle-before-grooming-cherry-creek-denver-october-2025.jpg";
import afterLabradoodle from "@/assets/labradoodle-after-grooming-cherry-creek-denver-october-2025.jpg";
import afterCockapoo from "@/assets/cockapoo-after-grooming-denver.jpg";
import afterBlackPoodle from "@/assets/black-poodle-after-grooming-denver.jpg";
import afterPyreneesDeshedding from "@/assets/great-pyrenees-after-deshedding-denver.jpg";
import beforePyrenees from "@/assets/great-pyrenees-before-grooming-denver.jpg";
import afterPyrenees from "@/assets/great-pyrenees-after-grooming-denver.jpg";
import beforeSchnauzer from "@/assets/miniature-schnauzer-before-grooming-highlands-ranch-denver.jpg";
import afterSchnauzer from "@/assets/miniature-schnauzer-after-grooming-highlands-ranch-denver.jpg";
import testimonialGolden from "@/assets/happy-golden-retriever-after-grooming-denver.jpg";
import testimonialCat from "@/assets/happy-persian-cat-after-grooming-denver.jpg";
import testimonialPoodle from "@/assets/happy-poodle-after-grooming-denver.jpg";
import testimonialLab from "@/assets/happy-senior-lab-after-grooming-denver.jpg";
import petOfTheMonth from "@/assets/featured-pet-grooming-transformation-denver.jpg";
import ServiceAreasSection from "@/components/ServiceAreasSection";
import GoogleMap from "@/components/GoogleMap";

const Index = () => {
  const [showStickyBar, setShowStickyBar] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowStickyBar(window.scrollY > 500);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  // All transformations for carousel and gallery
  const allTransformations = [
    {
      before: beforePyrenees,
      after: afterPyrenees,
      petType: "Great Pyrenees",
      description: "Complete deshedding & grooming transformation",
      beforeAlt: "Great Pyrenees before professional mobile grooming in Denver - heavy shedding and matted coat",
      afterAlt: "Great Pyrenees after professional deshedding and grooming service in Denver - clean and beautifully groomed"
    },
    {
      before: beforeSchnauzer,
      after: afterSchnauzer,
      petType: "Miniature Schnauzer",
      description: "Puppy Cut in Highlands Ranch",
      beforeAlt: "Miniature Schnauzer puppy before first grooming in Highlands Ranch Denver",
      afterAlt: "Miniature Schnauzer puppy after professional puppy cut in Highlands Ranch Denver"
    },
    {
      before: afterHavanese,
      after: beforeHavanese,
      petType: "Havanese",
      description: "Full Groom & Styling",
      beforeAlt: "Havanese after professional grooming and styling in Denver - fresh teddy bear cut",
      afterAlt: "Havanese before professional mobile grooming in Denver - shaggy coat needing trim"
    },
    {
      before: beforeLabradoodleV2,
      after: afterLabradoodleV2,
      petType: "Labradoodle",
      description: "Complete Grooming & Styling",
      beforeAlt: "Labradoodle before professional mobile grooming in Denver - matted curly coat",
      afterAlt: "Labradoodle after professional grooming and styling in Denver - clean and beautifully trimmed"
    },
    {
      before: afterBlackPoodle,
      after: afterBlackPoodle,
      petType: "Standard Poodle",
      description: "Spa Bath & Trim",
      beforeAlt: "Black Standard Poodle after spa bath and trim in Denver mobile grooming van",
      afterAlt: "Beautiful black Standard Poodle after luxury spa bath and trim service in Denver"
    },
    {
      before: afterPyreneesDeshedding,
      after: afterPyreneesDeshedding,
      petType: "Great Pyrenees",
      description: "Designer Cut",
      beforeAlt: "Great Pyrenees after complete deshedding service in Denver showing massive fur removal",
      afterAlt: "Happy Great Pyrenees after professional deshedding and grooming in Denver mobile van"
    },
    {
      before: afterCockapoo,
      after: afterCockapoo,
      petType: "Cockapoo Duo",
      description: "Full Service Groom",
      beforeAlt: "Two happy Cockapoos after full service mobile grooming in Denver",
      afterAlt: "Two adorable Cockapoos after complete full service groom in Denver mobile van"
    }
  ];
  
  // Split transformations: first 4 for carousel, rest for masonry gallery
  const carouselTransformations = allTransformations.slice(0, 4);
  const galleryTransformations = allTransformations.slice(4).map(t => ({
    after: t.after,
    afterAlt: t.afterAlt,
    petType: t.petType,
    description: t.description
  }));
  
  const services = [{
    icon: <Scissors className="h-8 w-8" />,
    title: "Full Groom",
    seoTitle: "Full Groom in Denver – Mobile Dog Haircuts & Styling",
    description: "Complete grooming service including wash, cut, and style",
    link: "/services/full-groom"
  }, {
    icon: <Sparkles className="h-8 w-8" />,
    title: "Spa Bath",
    seoTitle: "Bath & Brush Grooming in Denver – Mobile Spa Baths",
    description: "Luxury spa bath with premium shampoos and conditioning",
    link: "/services/spa-bath"
  }, {
    icon: <Heart className="h-8 w-8" />,
    title: "Puppy Groom",
    seoTitle: "Puppy Grooming in Denver – First-Time Gentle Grooming",
    description: "Gentle first grooming experience for puppies",
    link: "/services/puppy-cut"
  }, {
    icon: <Cat className="h-8 w-8" />,
    title: "Cat Grooming",
    seoTitle: "Cat Grooming in Denver – Lion Cuts & Mat Removal",
    description: "Specialized feline care including lion cuts, mat removal, and gentle handling",
    link: "/services/cat-grooming"
  }];
  const whyChooseUs = [{
    icon: <Award className="h-6 w-6" />,
    title: "Certified Groomers",
    description: "Professional certification and years of experience"
  }, {
    icon: <Shield className="h-6 w-6" />,
    title: "Anxiety-Free Experience",
    description: "Calm, comfortable environment in familiar surroundings"
  }, {
    icon: <Sparkles className="h-6 w-6" />,
    title: "Hypoallergenic Options",
    description: "Organic, natural products for sensitive pets"
  }, {
    icon: <Cat className="h-6 w-6" />,
    title: "Feline Grooming Experts",
    description: "Specialized cat handling techniques and anxiety-reducing methods"
  }];
  const testimonials = [{
    name: "Sarah Mitchell",
    location: "Cherry Creek",
    text: "Groomie Roomie transformed my anxious Golden Retriever into a spa-loving pup! The mobile service is a game-changer.",
    rating: 5,
    petImage: testimonialGolden
  }, {
    name: "David Chen",
    location: "Highlands Ranch",
    text: "Professional, punctual, and my Persian cat actually enjoys grooming now. Worth every penny!",
    rating: 5,
    petImage: testimonialCat
  }, {
    name: "Maria Rodriguez",
    location: "LoDo",
    text: "The convenience of mobile grooming plus luxury spa quality? Yes please! My poodle looks like a show dog.",
    rating: 5,
    petImage: testimonialPoodle
  }, {
    name: "Jennifer Adams",
    location: "Lakewood",
    text: "Finally found groomers who understand senior dogs. They treated my 12-year-old lab with such gentle care.",
    rating: 5,
    petImage: testimonialLab
  }];
  
  return (
    <div className="min-h-screen bg-background scroll-smooth">
      <SEOHead
        title="Mobile Dog & Cat Grooming in Denver"
        description="Luxury mobile dog & cat grooming across Denver. Stress-free grooming at your doorstep. Book today!"
        keywords="mobile dog grooming denver, mobile cat grooming denver, pet grooming denver, mobile grooming, dog groomer denver, cat grooming denver"
        canonical="https://groomieroomie.com"
      />
      
      <OrganizationSchema />
      <ServiceAreaSchema />
      <BreadcrumbSchema items={[{ name: "Home", url: "/" }]} />
      <AggregateReviewsSchema reviews={[
        { author: testimonials[0].name, rating: testimonials[0].rating, reviewBody: testimonials[0].text, datePublished: "2024-03-15", location: testimonials[0].location },
        { author: testimonials[1].name, rating: testimonials[1].rating, reviewBody: testimonials[1].text, datePublished: "2024-06-22", location: testimonials[1].location },
        { author: testimonials[2].name, rating: testimonials[2].rating, reviewBody: testimonials[2].text, datePublished: "2024-09-08", location: testimonials[2].location },
        { author: testimonials[3].name, rating: testimonials[3].rating, reviewBody: testimonials[3].text, datePublished: "2024-11-30", location: testimonials[3].location }
      ]} />
      
      <Navigation />
      
      {/* New client offer banner */}
      <SeasonalBanner
        id="new-client-special"
        message="✨ New Client Special: Get 10% off your first grooming!"
        ctaText="Claim 10% Off"
        ctaLink="/booking?promo=FIRST10"
        variant="promotion"
      />
      
      <CookieBanner />
      
      {/* Hero Section - Condensed for mobile, full on desktop */}
      <section className="relative pt-24 md:pt-28 pb-8 md:pb-20 lg:pb-32 overflow-hidden">
        <div className="absolute inset-0 gradient-hero opacity-10 z-0 pointer-events-none" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="space-y-6 md:space-y-8">
              <div className="space-y-3 md:space-y-4">
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold leading-tight">
                  Mobile Dog & Cat Grooming in Denver
                </h1>
                <p className="text-lg md:text-xl text-muted-foreground max-w-xl">
                  Professional dog and cat grooming in Cherry Creek and across Denver. Our fully equipped mobile salon brings the spa experience to your home, keeping your pets comfortable and stress-free.
                </p>
              </div>

              <div className="flex items-center gap-2 mb-1 p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl text-sm text-amber-800 dark:text-amber-300 max-w-md">
                <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse flex-shrink-0" />
                <span><strong>High demand this week</strong> — book ahead to secure your slot</span>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  variant="hero" 
                  size="lg" 
                  asChild
                  data-gtm-category="homepage"
                  data-gtm-action="click_book_now"
                  data-gtm-label="Hero - Book Now in Denver"
                >
                  <Link 
                    to="/booking"
                    onClick={() => trackCTAClick('Book Now in Denver', 'homepage_hero', '/booking')}
                  >
                    Book Now in Denver <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button 
                  variant="outline" 
                  size="lg" 
                  asChild
                  data-gtm-category="homepage"
                  data-gtm-action="click_view_pricing"
                  data-gtm-label="Hero - View Pricing"
                >
                  <Link 
                    to="/pricing"
                    onClick={() => trackCTAClick('View Pricing', 'homepage_hero', '/pricing')}
                  >
                    View Pricing
                  </Link>
                </Button>
              </div>
              
              {/* Quick link to locations */}
              <Link 
                to="/locations" 
                className="inline-flex items-center text-sm text-primary hover:underline"
                onClick={() => trackNavigation('locations', 'homepage_hero', '/locations')}
              >
                <MapPin className="h-4 w-4 mr-1" />
                See All Locations We Serve
              </Link>
              
              {/* Trust badges - horizontal scroll on mobile */}
              <div className="flex items-center gap-4 text-sm text-muted-foreground overflow-x-auto pb-2 scrollbar-hide md:overflow-visible md:gap-6">
                <div className="flex items-center gap-1 whitespace-nowrap">
                  <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                  <span>Same-day booking</span>
                </div>
                <div className="flex items-center gap-1 whitespace-nowrap">
                  <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                  <span>40-minute service area</span>
                </div>
                <div className="flex items-center gap-1 whitespace-nowrap">
                  <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                  <span>Anxiety-free experience</span>
                </div>
              </div>
            </div>
            
            {/* Van image - hidden on mobile, visible on lg+ */}
            <div className="relative hidden lg:block">
              <img 
                src={groomingVan} 
                alt="Mobile pet grooming van providing at-home dog and cat grooming services in Denver Cherry Creek Highlands and Capitol Hill neighborhoods | Groomie Roomie" 
                className="rounded-2xl shadow-luxury transform hover:scale-105 transition-transform duration-300"
                width={700}
                height={500}
                fetchPriority="high"
              />
              <div className="absolute -bottom-4 -right-4 bg-accent text-accent-foreground p-4 rounded-full shadow-accent">
                <Heart className="h-8 w-8" fill="currentColor" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Before/After Gallery Preview - Hero Showcase + Dynamic Grid */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-primary/5 via-transparent to-accent/5 relative overflow-hidden">
        {/* Additional Diagonal SVG Background */}
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-br from-accent/10 to-transparent -skew-y-2 transform origin-top-left" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <div className="text-center mb-8 md:mb-12 space-y-3 md:space-y-4">
            <h2 className="text-2xl md:text-5xl font-bold">
              From Ruff to <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Radiant</span> ✨
            </h2>
            <p className="text-base md:text-xl text-muted-foreground max-w-3xl mx-auto">
              See the incredible before and after results from our professional mobile grooming services. 
              Real transformations from real Denver pets.
            </p>
          </div>

          {/* Auto-Play Carousel Showcase */}
          <TransformationShowcase 
            transformations={carouselTransformations}
          />

          {/* CTA to Gallery */}
          <div className="text-center mt-8 md:mt-12 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="outline" size="lg" className="group" asChild>
                <Link 
                  to="/gallery"
                  onClick={() => trackNavigation('gallery', 'homepage_before_after_section', '/gallery')}
                >
                  View Full Gallery 
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
              <Button variant="ghost" size="lg" asChild>
                <Link 
                  to="/pricing"
                  onClick={() => trackNavigation('pricing', 'homepage_before_after_section', '/pricing')}
                >
                  See Service Pricing
                </Link>
              </Button>
            </div>
            <Link 
              to="/services/full-groom"
              className="inline-flex items-center text-sm text-muted-foreground hover:text-primary transition-colors"
              onClick={() => trackNavigation('full_groom', 'homepage_before_after_section', '/services/full-groom')}
            >
              Explore Our Full Groom Services <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Happy Pets Gallery - Visual proof of volume */}
      <HappyPetsGallery additionalTransformations={galleryTransformations} />

      {/* Services Grid */}
      <section className="py-12 md:py-20 bg-warm-white/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold mb-3 md:mb-4">Premium Mobile Pet Grooming Services in Denver</h2>
            <p className="text-base md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Professional grooming services designed for your pet's comfort and your convenience
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
            {services.map((service, index) => (
              <Link 
                key={index} 
                to={service.link}
                onClick={() => trackNavigation(service.title.toLowerCase().replace(' ', '_'), 'homepage_services_grid', service.link)}
              >
                <Card className="text-center p-4 md:p-6 hover:shadow-accent transition-all duration-300 transform hover:scale-105 h-full">
                  <CardContent className="space-y-2 md:space-y-4 p-0">
                    <div className="w-12 h-12 md:w-16 md:h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center text-primary">
                      {service.icon}
                    </div>
                    <h3 className="text-base md:text-xl font-semibold">{service.title}</h3>
                    <p className="text-xs md:text-base text-muted-foreground">{service.description}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          
          {/* Additional Service Links */}
          <div className="mt-8 flex flex-wrap justify-center gap-4 text-sm">
            <Link 
              to="/services/senior-care" 
              className="text-muted-foreground hover:text-primary transition-colors"
              onClick={() => trackNavigation('senior_care', 'homepage_services_section', '/services/senior-care')}
            >
              Senior Pet Grooming – Arthritis-Friendly Care
            </Link>
            <Link 
              to="/services/deshedding" 
              className="text-muted-foreground hover:text-primary transition-colors"
              onClick={() => trackNavigation('deshedding', 'homepage_services_section', '/services/deshedding')}
            >
              Deshedding Treatment – Reduce Shedding 80%
            </Link>
          </div>
          
          <div className="text-center mt-8 md:mt-12 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="outline" size="lg" asChild>
                <Link 
                  to="/services"
                  onClick={() => trackNavigation('services', 'homepage_services_section', '/services')}
                >
                  View All Services <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button variant="ghost" size="lg" asChild>
                <Link 
                  to="/pricing"
                  onClick={() => trackNavigation('pricing', 'homepage_services_section', '/pricing')}
                >
                  View Pricing
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Homepage FAQ Section - Under Services, Above Main CTA */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <details className="group border border-border rounded-lg bg-card overflow-hidden">
              <summary className="cursor-pointer px-6 py-4 font-semibold text-lg flex items-center justify-between hover:bg-muted/50 transition-colors list-none">
                <span>Is mobile grooming safe?</span>
                <span className="ml-4 text-primary transition-transform group-open:rotate-180">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
              </summary>
              <div className="px-6 pb-4 text-muted-foreground leading-relaxed">
                Yes. Mobile grooming is often safer and calmer than traditional salons because it is quiet, private, cage-free, and one-on-one. Many anxious, senior, and sensitive pets feel much more relaxed in a mobile grooming environment.
              </div>
            </details>
            <details className="group border border-border rounded-lg bg-card overflow-hidden">
              <summary className="cursor-pointer px-6 py-4 font-semibold text-lg flex items-center justify-between hover:bg-muted/50 transition-colors list-none">
                <span>Do you groom cats?</span>
                <span className="ml-4 text-primary transition-transform group-open:rotate-180">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
              </summary>
              <div className="px-6 pb-4 text-muted-foreground leading-relaxed">
                Yes, we provide full professional cat grooming including baths, brushing, lion cuts, mat removal, nail trimming, and gentle handling in a calm, quiet setting.
              </div>
            </details>
            <details className="group border border-border rounded-lg bg-card overflow-hidden">
              <summary className="cursor-pointer px-6 py-4 font-semibold text-lg flex items-center justify-between hover:bg-muted/50 transition-colors list-none">
                <span>How long does grooming take?</span>
                <span className="ml-4 text-primary transition-transform group-open:rotate-180">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
              </summary>
              <div className="px-6 pb-4 text-muted-foreground leading-relaxed">
                Most appointments take between 60–90 minutes depending on size, coat condition, temperament, and service type. Some pets may need extra time, especially seniors, puppies, or heavily matted coats.
              </div>
            </details>
            <details className="group border border-border rounded-lg bg-card overflow-hidden">
              <summary className="cursor-pointer px-6 py-4 font-semibold text-lg flex items-center justify-between hover:bg-muted/50 transition-colors list-none">
                <span>Will I know the price before you start?</span>
                <span className="ml-4 text-primary transition-transform group-open:rotate-180">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
              </summary>
              <div className="px-6 pb-4 text-muted-foreground leading-relaxed">
                Yes, pricing is always discussed upfront. If coat condition, behavior, or matting requires additional work, we will always talk to you first. No surprises.
              </div>
            </details>
          </div>
        </div>
      </section>
      
      {/* Homepage FAQ Schema */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Is mobile grooming safe?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes. Mobile grooming is often safer and calmer than traditional salons because it is quiet, private, cage-free, and one-on-one. Many anxious, senior, and sensitive pets feel much more relaxed in a mobile grooming environment."
                }
              },
              {
                "@type": "Question",
                "name": "Do you groom cats?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, we provide full professional cat grooming including baths, brushing, lion cuts, mat removal, nail trimming, and gentle handling in a calm, quiet setting."
                }
              },
              {
                "@type": "Question",
                "name": "How long does grooming take?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Most appointments take between 60–90 minutes depending on size, coat condition, temperament, and service type. Some pets may need extra time, especially seniors, puppies, or heavily matted coats."
                }
              },
              {
                "@type": "Question",
                "name": "Will I know the price before you start?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, pricing is always discussed upfront. If coat condition, behavior, or matting requires additional work, we will always talk to you first. No surprises."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Trust Badges Section */}
      <TrustBadges />

      {/* Testimonials - Moved up for social proof */}
      <section className="py-12 md:py-20 bg-warm-white/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold mb-3 md:mb-4">Denver Pet Parent Reviews</h2>
            <p className="text-base md:text-xl text-muted-foreground">
              See what Denver pet families are saying about our mobile grooming service
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
            {testimonials.map((testimonial, index) => <Card key={index} className="p-4 md:p-6 shadow-soft hover:shadow-accent transition-all duration-300">
                <CardContent className="space-y-3 md:space-y-4 p-0">
                  <div className="flex items-start space-x-3 md:space-x-4">
                    <div className="flex-shrink-0">
                      <img 
                        src={testimonial.petImage} 
                        alt={`${testimonial.location} pet after professional mobile grooming service in Denver showing happy results`} 
                        className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2 border-accent/20"
                        width={64}
                        height={64}
                        loading="lazy"
                      />
                    </div>
                    <div className="flex-1 space-y-2 md:space-y-3">
                      <div className="flex items-center space-x-1">
                        {[...Array(testimonial.rating)].map((_, i) => <Star key={i} className="h-4 w-4 md:h-5 md:w-5 text-luxury-gold" fill="currentColor" />)}
                      </div>
                      <p className="text-sm md:text-base text-muted-foreground italic">"{testimonial.text}"</p>
                      <div>
                        <p className="font-semibold text-sm md:text-base">{testimonial.name}</p>
                        <p className="text-xs md:text-sm text-muted-foreground">{testimonial.location}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-primary/5 to-accent/5">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Get Grooming Tips & Exclusive Offers</h2>
          <p className="text-muted-foreground mb-6">
            Join our newsletter for weekly pet care tips, seasonal grooming advice, and subscriber-only discounts.
          </p>
          <NewsletterSignup source="homepage" className="max-w-md mx-auto" />
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src={happyDog} 
                alt="Happy dog after professional mobile grooming service in Denver" 
                className="rounded-2xl shadow-luxury"
                width={600}
                height={450}
                loading="lazy"
              />
            </div>
            
            <div className="space-y-6 md:space-y-8 order-1 lg:order-2">
              <div>
                <h2 className="text-2xl md:text-4xl font-bold mb-3 md:mb-4">Why Choose At-Home Pet Grooming?</h2>
                <p className="text-base md:text-xl text-muted-foreground">
                  We bring the spa to your pet, ensuring a stress-free experience with luxury results.
                </p>
              </div>
              
              <div className="space-y-4 md:space-y-6">
                {whyChooseUs.map((feature, index) => <div key={index} className="flex items-start space-x-3 md:space-x-4">
                    <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-full flex items-center justify-center text-primary">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="text-base md:text-lg font-semibold mb-1 md:mb-2">{feature.title}</h3>
                      <p className="text-sm md:text-base text-muted-foreground">{feature.description}</p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Loyalty Program Promo - Moved lower for retention focus */}
      <section className="py-12 md:py-16 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-2 border-primary/20">
            <CardContent className="p-6 md:p-12">
              <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
                    <Star className="h-5 w-5 text-primary" />
                    <span className="text-sm font-semibold text-primary">Pawsome Rewards</span>
                  </div>
                  <h2 className="text-2xl md:text-4xl font-bold mb-3 md:mb-4">
                    Earn Points on Every Groom! 🎁
                  </h2>
                  <p className="text-base md:text-lg text-muted-foreground mb-4 md:mb-6">
                    Join our loyalty program and get 50 bonus points instantly! Earn more on every grooming, review, and referral.
                  </p>
                  <ul className="space-y-2 md:space-y-3 mb-4 md:mb-6">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-sm md:text-base">100 points per grooming</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-sm md:text-base">200 points per referral</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <span className="text-sm md:text-base">50 bonus points just for joining!</span>
                    </li>
                  </ul>
                  
                  {/* Email Signup Form */}
                  <LoyaltySignupForm />
                  
                  <p className="text-sm text-muted-foreground mt-4">
                    <Link 
                      to="/loyalty"
                      className="text-primary hover:underline"
                      onClick={() => trackNavigation('loyalty_program', 'homepage_loyalty_section', '/loyalty')}
                    >
                      Learn more about Pawsome Rewards →
                    </Link>
                  </p>
                </div>
                <div className="hidden md:block">
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="bg-gradient-to-br from-primary/20 to-primary/5">
                      <CardContent className="p-6 text-center">
                        <div className="text-4xl font-bold text-primary mb-2">50</div>
                        <div className="text-sm text-muted-foreground">Welcome Bonus</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-accent/20 to-accent/5">
                      <CardContent className="p-6 text-center">
                        <div className="text-4xl font-bold text-accent mb-2">100</div>
                        <div className="text-sm text-muted-foreground">Per Grooming</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-secondary/20 to-secondary/5">
                      <CardContent className="p-6 text-center">
                        <div className="text-4xl font-bold text-secondary mb-2">200</div>
                        <div className="text-sm text-muted-foreground">Per Referral</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-primary/20 to-accent/20">
                      <CardContent className="p-6 text-center">
                        <div className="text-4xl font-bold text-primary mb-2">VIP</div>
                        <div className="text-sm text-muted-foreground">Tier Benefits</div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>



      <FAQSchema questions={[
        {
          question: "How much does mobile dog grooming cost in Denver?",
          answer: "Prices start at $75 for basic grooming and vary based on your pet's size, breed, and service needs. Full-service grooming packages range from $85-$165. We provide transparent pricing with no hidden fees."
        },
        {
          question: "What areas in Denver do you serve?",
          answer: "We serve all Denver neighborhoods including Cherry Creek, Highlands, Capitol Hill, LoDo, RiNo, Washington Park, and surrounding areas within a 40-minute drive of downtown Denver."
        },
        {
          question: "Do you groom cats as well as dogs?",
          answer: "Yes! We specialize in both dog and cat grooming. Our groomers are trained in feline-specific handling techniques and offer services including lion cuts, mat removal, and anxiety-reducing grooming methods for cats."
        },
        {
          question: "How long does a mobile grooming appointment take?",
          answer: "Most appointments take 60-90 minutes depending on your pet's size, coat condition, and services selected. We focus on one pet at a time to ensure a calm, personalized experience."
        },
        {
          question: "Is mobile grooming better for anxious pets?",
          answer: "Absolutely! Mobile grooming eliminates travel stress, unfamiliar environments, and interactions with other animals. Your pet stays in their familiar home environment, resulting in a significantly calmer grooming experience."
        }
      ]} />

      {/* Service Areas Section */}
      <ServiceAreasSection />

      {/* Google Maps Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">Visit Our Location</h2>
            <p className="text-xl text-muted-foreground">
              3144 W Frances Walsh Pl, Denver, CO 80236
            </p>
          </div>
          <GoogleMap 
            embedUrl="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d19241.53833819484!2d-104.9900536!3d39.7302588!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4593b3aff9944c21%3A0x1a9aacdfa896b843!2sGroomie%20Roomie!5e1!3m2!1sen!2slv!4v1761051841516!5m2!1sen!2slv"
            height="500px"
            className="shadow-accent"
          />
        </div>
      </section>

      <Footer />
      {showStickyBar && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-primary text-white py-2.5 px-4 flex items-center justify-between shadow-lg" style={{animation: 'slideDown 0.2s ease-out'}}>
          <span className="text-sm font-medium hidden sm:block">Mobile Dog & Cat Grooming · Denver</span>
          <span className="text-sm font-medium sm:hidden">Groomie Roomie · Denver</span>
          <a href="/booking" className="bg-white text-primary text-xs font-semibold px-4 py-1.5 rounded-lg hover:bg-white/90 transition-colors">
            Book Now →
          </a>
        </div>
      )}
    </div>
  );
};

export default Index;